/**
 * A player's rank crest (specs/rank-on-social-screens §Story 2, design 1b "Shield Badge").
 * The art carries the tier; the rank name is the accessibility label and is never visible
 * text (AC-12) — same treatment as the shipped header cluster.
 *
 * Positioning belongs to the caller: this renders only the crest, with no chrome and no
 * layout of its own, so it can hang off an avatar's corner on any surface.
 */

import React, { useMemo } from 'react';
import { Image, type ImageStyle } from 'expo-image';
import type { StyleProp } from 'react-native';
import { getRank } from '../../helpers/rankRegistry.helper';
import { PLAYER_RANK_TEST_IDS } from '../../constants/playerRanks.constants';
import { getPlayerRankBadgeStyles } from './PlayerRankBadge.styles';

export interface PlayerRankBadgeProps {
  /** Rank index 1..10; clamped by the registry, so a bad payload degrades instead of crashing. */
  rankIndex: number;
  size: number;
  /** Absolute placement from the caller (corner offsets, zIndex). */
  style?: StyleProp<ImageStyle>;
  testID?: string;
}

export const PlayerRankBadge: React.FC<PlayerRankBadgeProps> = ({
  rankIndex,
  size,
  style,
  testID,
}) => {
  const styles = useMemo(() => getPlayerRankBadgeStyles(size), [size]);
  const rank = getRank(rankIndex);

  return (
    <Image
      source={rank.badge}
      style={[styles.badge, style]}
      contentFit="contain"
      cachePolicy="memory-disk"
      // Decorative-adjacent, but it is the only carrier of the rank, so it keeps a real label.
      accessibilityLabel={rank.name}
      alt={rank.name}
      // Never swallow the press of the card or row it sits on (AC-13).
      pointerEvents="none"
      testID={testID ?? PLAYER_RANK_TEST_IDS.badge}
    />
  );
};
